/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AttachmentAcceptedFileTypesAPIType,
  AttachmentCategoriesAPIType,
  AttachmentsAPIType
} from "shared/types/Attachment"
import { ProductPositionInstallationReferenceAPIType } from "shared/types/InstallationReference"
import {
  PriceAPIType,
  PriceComponentsAPIType,
  PriceGroupsAPIType,
  PriceListsAPIType,
  PricesAPIType
} from "shared/types/Price"
import { ProductInformationComponentsAPIType } from "shared/types/Product"
import {
  ProductConfigurationAPIType,
  ProductConfigurationAddonsAPIType,
  ProductConfigurationDimensioningAPIType,
  ProductConfigurationMetaAPIType,
  ProductConfigurationModelsAPIType,
  ProductConfigurationProductsAPIType,
  ProductConfigurationSpecialAttributesAPIType
} from "shared/types/ProductConfiguration"
import {
  ArticleNumberSearchResultsAPIType,
  DuplicateEntitiesAPIType,
  FavoritesAPIType,
  FloorsAPIType,
  InflationSurchargesAPIType,
  ProcessesAPIType,
  ProductInformationAPIType,
  ProductPositionsAPIType,
  ProjectHistoryEntriesAPIType,
  ProjectPhasesAPIType,
  ProjectsAPIType,
  RoomsAPIType,
  SpecialPriceGroupsAPIType,
  UserPermissionsAPIType
} from "shared/types/Project"
import {
  SendShareLinksAPIType,
  ShareLinksAPIType
} from "shared/types/ShareLink"

import { ClarificationRequestsAPIType } from "entities/clarification-requests/types/ClarificationRequest"
import { TeamInvitationsAPIType } from "entities/team-invitations/types/TeamInvitation"
import {
  TeamMembersAPIType,
  UsersAPIType
} from "entities/team-members/types/TeamMember"
import { TeamsAPIType } from "entities/teams/types/Team"
import JSONAPISerializer from "json-api-serializer"

import { mapAPITypeToTypename } from "./mapApiTypeToTypename"

export const serializer = new JSONAPISerializer({})

export const ExportsAPIType = "exports" as const

const defaultRegisterOptions: JSONAPISerializer.Options = {
  id: "id",
  beforeSerialize: (data: any) => ({
    ...data,
    __typename: undefined
  })
}

serializer.register(ProjectsAPIType, {
  ...defaultRegisterOptions,
  beforeSerialize: (data: any) => ({
    ...data,
    phase:
      data.phase || data.phase === null ? data.phase?.id || null : undefined,
    __typename: undefined,
    type: undefined
  }),
  relationships: {
    phase: { type: ProjectPhasesAPIType },
    specialPriceGroups: { type: SpecialPriceGroupsAPIType }
  }
})
serializer.register(ProjectPhasesAPIType, defaultRegisterOptions)

serializer.register(TeamsAPIType, defaultRegisterOptions)
serializer.register(TeamMembersAPIType, defaultRegisterOptions)
serializer.register(TeamInvitationsAPIType, defaultRegisterOptions)
serializer.register(UsersAPIType, defaultRegisterOptions)

serializer.register(SpecialPriceGroupsAPIType, defaultRegisterOptions)
serializer.register(RoomsAPIType, {
  ...defaultRegisterOptions,
  beforeSerialize: (data: any) => ({
    ...data,
    __typename: undefined,
    type: undefined
  }),
  relationships: {
    parent: { type: data => data.type }
  }
})

serializer.register(FloorsAPIType, {
  ...defaultRegisterOptions,
  beforeSerialize: (data: any) => ({
    ...data,
    __typename: undefined,
    type: undefined
  }),
  relationships: {
    parent: { type: data => data.type }
  }
})

serializer.register(InflationSurchargesAPIType, defaultRegisterOptions)
serializer.register(PriceComponentsAPIType, defaultRegisterOptions)
serializer.register(PriceAPIType, defaultRegisterOptions)
serializer.register(PricesAPIType, defaultRegisterOptions)
serializer.register(PriceGroupsAPIType, defaultRegisterOptions)

serializer.register(ProductPositionsAPIType, {
  ...defaultRegisterOptions,
  beforeSerialize: (data: any) => ({
    ...data,
    __typename: undefined,
    type: undefined
  }),
  relationships: {
    references: {
      type: ProductPositionInstallationReferenceAPIType
    },
    parent: { type: data => data.type }
  }
})
serializer.register(SpecialPriceGroupsAPIType, defaultRegisterOptions)
serializer.register(
  ProductPositionInstallationReferenceAPIType,
  defaultRegisterOptions
)
serializer.register(AttachmentsAPIType, defaultRegisterOptions)
serializer.register(AttachmentCategoriesAPIType, defaultRegisterOptions)
serializer.register(AttachmentAcceptedFileTypesAPIType, defaultRegisterOptions)
serializer.register(FavoritesAPIType, defaultRegisterOptions)
serializer.register(ProcessesAPIType, defaultRegisterOptions)
serializer.register(ShareLinksAPIType, defaultRegisterOptions)
serializer.register(SendShareLinksAPIType, {
  beforeSerialize: (data: any) => ({ ...data, __typename: undefined })
})
serializer.register(ArticleNumberSearchResultsAPIType, defaultRegisterOptions)
serializer.register(ProductConfigurationAPIType, defaultRegisterOptions)
serializer.register(ProductConfigurationProductsAPIType, defaultRegisterOptions)
serializer.register(ProductConfigurationAddonsAPIType, defaultRegisterOptions)
serializer.register(ProductConfigurationMetaAPIType, defaultRegisterOptions)
serializer.register(
  ProductConfigurationSpecialAttributesAPIType,
  defaultRegisterOptions
)
serializer.register(
  ProductConfigurationDimensioningAPIType,
  defaultRegisterOptions
)

serializer.register(ProductConfigurationModelsAPIType, {
  ...defaultRegisterOptions,
  relationships: {
    followUps: { type: ProductConfigurationModelsAPIType }
  }
})
serializer.register(DuplicateEntitiesAPIType, {
  ...defaultRegisterOptions,
  relationships: {
    source: {
      type: data => data.type
    },
    target: {
      type: data => data.type
    }
  }
})
serializer.register(PriceListsAPIType, defaultRegisterOptions)
serializer.register(ProductInformationComponentsAPIType, defaultRegisterOptions)

serializer.register(ExportsAPIType, defaultRegisterOptions)
serializer.register(ProductInformationAPIType, defaultRegisterOptions)
serializer.register(UserPermissionsAPIType, defaultRegisterOptions)
serializer.register(ProjectHistoryEntriesAPIType, {
  ...defaultRegisterOptions,
  afterDeserialize: data => {
    if (
      typeof data === "object" &&
      data !== null &&
      Object.hasOwn(data, "entityType")
    ) {
      return {
        ...data,
        entityType: mapAPITypeToTypename(
          (data as { entityType: string }).entityType
        )
      }
    }
    return data
  }
})

serializer.register(ClarificationRequestsAPIType, {
  ...defaultRegisterOptions,
  relationships: {
    lastUpdatedBy: { type: UsersAPIType }
  }
})

export const ProjectImportsAPIType = "project_imports" as const
serializer.register(ProjectImportsAPIType, defaultRegisterOptions)

export const ProjectImportS3UploadsAPIType =
  "project_import_s3_uploads" as const
serializer.register(ProjectImportS3UploadsAPIType, defaultRegisterOptions)
